import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaStar, FaCheckCircle, FaHeart } from 'react-icons/fa';
import { submitTicketFeedbackAPI, validateFeedbackTicketAPI } from '../services/serverService';

export default function PublicFeedbackPage() {
    const [searchParams] = useSearchParams();
    const ticketId = searchParams.get('ticketId');

    const [rating, setRating] = useState(0);
    const [hover, setHover] = useState(0);
    const [comments, setComments] = useState('');
    const [isResolvedConfirmed, setIsResolvedConfirmed] = useState(true);
    
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [error, setError] = useState('');

    const [isValidating, setIsValidating] = useState(true);

    useEffect(() => {
        const validateTicket = async () => {
            if (!ticketId) {
                setError('Invalid access link. Ticket ID is missing.');
                setIsValidating(false);
                return;
            }

            try {
                await validateFeedbackTicketAPI({ ticketId });
            } catch (err) {
                console.error("Validation error:", err);
                // Extract the specific error message thrown by the backend
                setError(err.message || 'Failed to validate ticket status. Please try again later.');
            } finally {
                setIsValidating(false);
            }
        };

        validateTicket();
    }, [ticketId]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (rating === 0) {
            setError('Please select a star rating.');
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            await submitTicketFeedbackAPI({
                ticketId,
                rating,
                comments,
                isResolvedConfirmed
            });
            setIsSuccess(true);
        } catch (err) {
            setError('Failed to submit feedback. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isValidating) {
        return (
            <div style={{ minHeight: '100vh', backgroundColor: '#f4f7fa', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <p style={{ color: '#4a5568', fontWeight: 'bold' }}>Validating ticket status...</p>
            </div>
        );
    }

    if (error && !ticketId) {
        return (
            <div style={{ minHeight: '100vh', backgroundColor: '#f4f7fa', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px', fontFamily: 'sans-serif' }}>
                <div style={{ backgroundColor: 'white', padding: '40px', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', textAlign: 'center' }}>
                    <h2 style={{ color: '#e53e3e' }}>Error</h2>
                    <p style={{ color: '#4a5568' }}>{error}</p>
                </div>
            </div>
        );
    }

    if (isSuccess) {
        return (
            <div style={{ minHeight: '100vh', backgroundColor: '#f4f7fa', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px', fontFamily: 'sans-serif' }}>
                <div style={{ backgroundColor: 'white', padding: '40px', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxWidth: '400px', width: '100%', textAlign: 'center' }}>
                    <FaHeart style={{ color: '#e53e3e', fontSize: '48px', marginBottom: '20px' }} />
                    <h2 style={{ color: '#2d3748', margin: '0 0 10px 0' }}>Thank You!</h2>
                    <p style={{ color: '#718096', margin: 0 }}>Your feedback helps us improve our support experience.</p>
                </div>
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh', backgroundColor: '#f4f7fa', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px', fontFamily: 'sans-serif' }}>
            <div style={{ backgroundColor: 'white', padding: '40px', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxWidth: '450px', width: '100%' }}>
                <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                    <FaCheckCircle style={{ fontSize: '42px', color: '#38a169', marginBottom: '10px' }} />
                    <h2 style={{ margin: 0, color: '#2d3748' }}>Ticket Resolved</h2>
                    <p style={{ margin: '5px 0 0 0', color: '#718096', fontSize: '14px' }}>Ticket: <strong>{ticketId}</strong></p>
                </div>

                {error && <div style={{ backgroundColor: '#fff5f5', color: '#c53030', padding: '10px', borderRadius: '6px', marginBottom: '20px', fontSize: '14px', border: '1px solid #feb2b2' }}>{error}</div>}

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div style={{ backgroundColor: '#f7fafc', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '14px', color: '#2d3748', fontWeight: 'bold' }}>
                            <input 
                                type="checkbox" 
                                checked={isResolvedConfirmed} 
                                onChange={(e) => setIsResolvedConfirmed(e.target.checked)} 
                                style={{ width: '18px', height: '18px', accentColor: '#38a169' }} 
                            />
                            I confirm this issue has been fully resolved.
                        </label>
                    </div>

                    <div style={{ textAlign: 'center' }}>
                        <p style={{ margin: '0 0 10px 0', fontWeight: 'bold', color: '#4a5568' }}>How would you rate your support experience?</p>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                            {[...Array(5)].map((star, index) => {
                                const ratingValue = index + 1;
                                return (
                                    <label key={index} style={{ cursor: 'pointer' }}>
                                        <input 
                                            type="radio" 
                                            name="rating" 
                                            value={ratingValue} 
                                            onClick={() => setRating(ratingValue)} 
                                            style={{ display: 'none' }}
                                        />
                                        <FaStar 
                                            style={{ fontSize: '32px', color: ratingValue <= (hover || rating) ? '#ecc94b' : '#e2e8f0', transition: 'color 0.2s' }} 
                                            onMouseEnter={() => setHover(ratingValue)} 
                                            onMouseLeave={() => setHover(0)} 
                                        />
                                    </label>
                                );
                            })}
                        </div>
                    </div>

                    <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 'bold', color: '#4a5568', marginBottom: '8px' }}>Any additional comments? (Optional)</label>
                        <textarea 
                            value={comments} 
                            onChange={(e) => setComments(e.target.value)} 
                            placeholder="Tell us what we did well, or how we can improve..."
                            style={{ width: '100%', boxSizing: 'border-box', padding: '12px', borderRadius: '6px', border: '1px solid #cbd5e0', minHeight: '100px', resize: 'vertical' }}
                        />
                    </div>

                    <button 
                        type="submit" 
                        disabled={isSubmitting || rating === 0} 
                        style={{ padding: '14px', backgroundColor: '#3182ce', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', fontSize: '16px', cursor: (isSubmitting || rating === 0) ? 'not-allowed' : 'pointer', opacity: (isSubmitting || rating === 0) ? 0.6 : 1 }}
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                    </button>
                </form>
            </div>
        </div>
    );
}